<template>
  <Transition name="shrink">
    <div
      v-show="true"
      :class="[
        'chapters-list__wrapper mt-32 mt-md-64',
        { 'chapters-list__wrapper--modal': modalVariant }
      ]"
    >
      <div
        ref="chaptersList"
        :class="['chapters-list', `chapters-list--${genre}`]"
      >
        <div
          v-if="isLoading"
          class="w-100 h-100 d-flex justify-content-center align-items-center"
        >
          <BaseSpinner
            :classes="`spinner__icon spinner__icon--${genre}`"
            type="grow"
          />
        </div>

        <template v-else-if="chapters.length">
          <div
            v-for="chapter in chapters"
            :key="chapter.order"
            :ref="isChapterActive(chapter) ? 'activeChapter' : 'chapter'"
            class="chapter"
          >
            <BaseButton
              variant="naked"
              :class="[
                'chapter__btn w-100 text-start d-flex justify-content-between',
                `chapter__btn--${genre}`,
                {
                  [`chapter__btn--active chapter__btn--active--${genre}`]:
                    isChapterActive(chapter)
                }
              ]"
              @click="onChapterClick(chapter)"
            >
              <div class="d-flex align-items-center overflow-hidden">
                <div
                  class="chapter__order text-label d-flex align-items-center justify-content-center"
                >
                  <PauseIcon
                    v-if="isChapterActive(chapter) && isPlaying"
                    class="pause-icon"
                    fill="white"
                    width="12"
                    height="12"
                  />
                  <PlayIcon
                    v-else-if="isChapterActive(chapter)"
                    class="play-icon"
                    fill="white"
                    stroke="white"
                    width="12"
                    height="12"
                  />
                  <span v-else>
                    {{ chapter.order }}
                  </span>
                </div>

                <div class="d-flex align-items-center overflow-hidden">
                  <span
                    class="chapter__name text-label text-truncate d-inline-block"
                  >
                    {{ chapter.chapter }}
                  </span>
                </div>
              </div>

              <div class="d-flex align-items-center">
                <span class="text-small">
                  {{ getChapterDuration(chapter.duration) }}
                </span>
              </div>
            </BaseButton>
          </div>
        </template>
      </div>
    </div>
  </Transition>
</template>

<script>
import PauseIcon from '~/assets/icons/pause.svg'
import PlayIcon from '~/assets/icons/play.svg'
import { formatTime } from '~/utils'
import { isElementVisible } from '~/utils/index'

export default {
  components: {
    PauseIcon,
    PlayIcon
  },
  props: {
    chapters: {
      type: Array,
      default: () => []
    },
    getAudiobookDetails: {
      type: Function,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    activeChapter: {
      type: Object,
      default: () => ({})
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    goTo: {
      type: Function,
      required: true
    },
    pause: {
      type: Function,
      required: true
    },
    modalVariant: {
      type: Boolean,
      default: false
    },
    genre: {
      type: String,
      required: true
    }
  },
  watch: {
    activeChapter() {
      this.$nextTick(() => this.checkIfChapterVisible())
    }
  },
  async mounted() {
    if (!this.chapters.length) {
      await this.getAudiobookDetails()
    }

    setTimeout(() => this.scrollToActiveChapter(), 100)
  },
  methods: {
    getChapterDuration(time) {
      return formatTime(time)
    },
    onChapterClick(chapter) {
      if (this.isPlaying && this.isChapterActive(chapter)) {
        this.pause()
      } else {
        this.goTo(chapter.order - 1, true)
      }
    },
    scrollToActiveChapter() {
      if (this.$refs.activeChapter) {
        try {
          const activeChapter = this.$refs.activeChapter[0]
          if (this.modalVariant) {
            activeChapter.scrollIntoView()
          } else if (activeChapter.offsetTop > 15) {
            this.$refs.chaptersList.scrollTop = activeChapter.offsetTop - 8
          }
          // eslint-disable-next-line no-empty
        } catch {}
      }
    },
    checkIfChapterVisible() {
      try {
        if (
          !isElementVisible(
            this.$refs.activeChapter[0],
            this.$refs.chaptersList
          )
        ) {
          this.scrollToActiveChapter()
        }
        // eslint-disable-next-line no-empty
      } catch {}
    },
    isChapterActive(chapter) {
      return chapter.order === this.activeChapter.order
    }
  }
}
</script>

<style lang="scss" scoped>
.shrink-leave-active {
  transition: all 0.3s ease-in;
  transform: scaleY(1);
  height: 327px;
}

.shrink-leave-to {
  transform: scaleY(0);
  height: 0;
  opacity: 0;
}

@keyframes grow {
  0% {
    opacity: 0;
    height: 0;
  }

  100% {
    opacity: 1;
    height: 327px;
  }
}

.chapters-list {
  animation: grow 0.5s forwards ease-out;
  position: relative;
  height: 327px;
  overflow-y: scroll;
  padding-top: 16px;
  margin-left: -16px;
  margin-right: -16px;
}

@include generate-genre-classes(
  'background-color',
  'light',
  'chapters-list',
  0.5
);

.chapters-list__wrapper--modal {
  .chapters-list {
    animation: none;
    height: unset;
    margin-top: 0;
  }
}

.chapters-list {
  &::after {
    content: '';
    position: sticky;
    bottom: 0;
    display: block;
    width: 100%;
    height: 56px;
    background: linear-gradient(
      180deg,
      rgba(226, 231, 234, 0) 0%,
      rgba(101, 105, 108, 0.2) 62.21%
    );
  }
}

.chapter {
  padding: 0 22% 0 16px;
}

@mixin generate-chapter-on-hover-classes() {
  @each $genre, $colors in $genres-colors {
    $color: map-get($colors, 'light');

    .chapter__btn--#{$genre}:hover {
      &:not(.chapter__btn--active) {
        background-color: rgba($color, 1);
      }
    }
  }
}

@include generate-chapter-on-hover-classes();

.chapter__btn {
  min-height: unset;
  padding: 8px;
  border: none;
  border-radius: 0;

  &--active,
  &--active:hover {
    color: $white;
  }
}

@include generate-genre-classes(
  'background-color',
  'dark',
  'chapter__btn--active',
  1
);

.chapter__name {
  margin-left: 10px;
}

.chapter__order {
  min-width: 24px;
  min-height: 24px;
}

.pause-icon {
  margin-bottom: 2px;
}

.spinner__icon {
  width: 6rem;
  height: 6rem;
}

@include generate-genre-classes(
  'background-color',
  'dark',
  'spinner__icon',
  0.7
);

@include media-breakpoint-down(lg) {
  .chapter {
    padding: 0 16px;
  }

  .chapter__btn {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@include media-breakpoint-down(sm) {
  .chapter {
    padding: 0;
  }
}
</style>
